import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Store,
  Search,
  MapPin,
  Award,
  ExternalLink,
  Filter,
  DollarSign,
  Loader2,
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

import { merchantService } from "../../../services/apiService";

// Fix lỗi thiếu Icon mặc định của Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Custom Icon cho Cụm Marker (Cluster)
const createCustomClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  return L.divIcon({
    html: `
      <div class="relative bg-white text-slate-900 font-bold text-sm px-3 py-1.5 rounded-xl shadow-md border border-slate-200 flex items-center justify-center min-w-[42px]">
        ${count}
        <div class="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white rotate-45 border-r border-b border-slate-200"></div>
      </div>
    `,
    className: "custom-cluster-icon",
    iconSize: L.point(45, 35, true),
  });
};

// 🎯 MAP CONTROLLER: TỰ ĐỘNG ZOOM VÀ MỞ POPUP KHI CLICK CHỌN DỰ ÁN
const MapController = ({ selectedShop, markerRefs }) => {
  const map = useMap();

  useEffect(() => {
    if (selectedShop && selectedShop.lat && selectedShop.lng) {
      const shopKey = selectedShop.terminalID || selectedShop.id;

      // 1. Zoom sâu vào tọa độ (Mức zoom 16) để tự động rã cụm Cluster
      map.flyTo([selectedShop.lat, selectedShop.lng], 16, {
        duration: 1.2,
      });

      // 2. Chờ hiệu ứng bay xong sẽ kích hoạt mở Popup
      const timer = setTimeout(() => {
        const marker = markerRefs.current[shopKey];
        if (marker) {
          marker.openPopup();
        }
      }, 1200);

      return () => clearTimeout(timer);
    }
  }, [selectedShop, map, markerRefs]);

  return null;
};

// Style Badge cho từng Nhóm Merchant
const SEGMENT_COLORS = {
  Micro: "bg-slate-100 text-slate-700 border-slate-300",
  Small: "bg-blue-50 text-blue-700 border-blue-200",
  Medium: "bg-amber-50 text-amber-700 border-amber-200",
  Large: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const WorkspacesManagerNew = () => {
  const navigate = useNavigate();

  // Ref lưu danh sách Marker
  const markerRefs = useRef({});

  // State Dữ liệu & API
  const [shopsData, setShopsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // State Phân trang
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(200);
  const [paginationInfo, setPaginationInfo] = useState({
    page: 1,
    limit: 200,
    totalCount: 0,
    totalPage: 1,
  });

  // State Bộ lọc & Chọn cửa hàng
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSegment, setSelectedSegment] = useState("ALL");
  const [selectedShop, setSelectedShop] = useState(null);

  const defaultCenter = [21.028511, 105.804817];

  // Call API Lấy danh sách cửa hàng
  const fetchData = useCallback(
    async (page = currentPage) => {
      // Reset segment for calling API
      let segment = selectedSegment;
      if (selectedSegment === 'ALL')
        segment = ''
      // Limit 4 characters length for merchant ID search
      let merchantId = searchTerm
      if (searchTerm.length < 4 && searchTerm.length !== 0) {
        merchantId = '';
        return;
      }
      setLoading(true);
      setError("");

      try {
        const params = { page: page, limit: pageSize, merchant_tier: segment, merchant_id: merchantId };
        const workspaceRes = await merchantService.getWorkspace(params).catch((e) => {
          console.warn("Lỗi API workspace:", e.message);
          return null;
        });

        if (!workspaceRes) {
          setError("Không thể kết nối đến máy chủ. Vui lòng thử lại!");
          return;
        }

        const resData = workspaceRes?.data || workspaceRes;
        const rawArray = resData?.merchants || [];
        const pag = resData?.pagination || {
          page: page,
          limit: pageSize,
          totalCount: rawArray.length,
          totalPage: 1,
        };

        setPaginationInfo(pag);

        const formattedShops = rawArray.map((shop, index) => {
          const formattedRank =
            shop.overallRank && shop.totalMerchantCount
              ? `${shop.overallRank.toLocaleString()}/${shop.totalMerchantCount.toLocaleString()}`
              : shop.rank || "N/A";

          return {
            ...shop,
            id: shop.workspaceId || shop.id || index,
            terminalID: shop.terminalID || shop.terminal_id || null,
            workspaceName: shop.workspaceName || shop.merchant_name || `Cửa hàng ${index + 1}`,
            merchantAddress: shop.merchantAddress || shop.address || "Chưa cập nhật địa chỉ",
            totalRevenue: shop.grandRevenue ?? shop.totalRevenue ?? shop.total_revenue ?? 0,
            avgRevenue: shop.avgRevenue ?? 0,
            segment: shop.merchantTier || shop.segment || "Micro",
            rank: formattedRank,
            lat: Number(shop.latitude ?? shop.lat) || 21.028511,
            lng: Number(shop.longitude ?? shop.lng) || 105.804817,
          };
        });

        setShopsData(formattedShops);
      } catch (err) {
        console.error("Lỗi xử lý dữ liệu workspace:", err);
        setError("Có lỗi xảy ra khi tải danh sách cửa hàng. Vui lòng thử lại!");
      } finally {
        setLoading(false);
      }
    },
    [currentPage, pageSize, selectedSegment, searchTerm]
  );

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage, fetchData]);

  // Filter Client
  // const filteredShops = useMemo(() => {
  //   return shopsData.filter((shop) => {
  //     const term = searchTerm.toLowerCase();
  //     const matchesSearch =
  //       shop.workspaceName?.toLowerCase().includes(term) ||
  //       (shop.terminalID && shop.terminalID.toLowerCase().includes(term)) ||
  //       shop.merchantAddress?.toLowerCase().includes(term);

  //     const matchesSegment = selectedSegment === "ALL" || shop.segment === selectedSegment;

  //     return matchesSearch && matchesSegment;
  //   });
  // }, [shopsData, searchTerm, selectedSegment]);

  // Chuyển trang Revenue
  const handleNavigateToRevenue = (terminalID, e) => {
    if (e) e.stopPropagation();
    if (!terminalID) return;
    navigate(`/revenue?terminalId=${terminalID}`);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(amount);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= paginationInfo.totalPage) {
      setCurrentPage(newPage);
    }
  };

  const handleSelectedSegment = (seg) => {
    setSelectedSegment(seg);
    if (searchTerm.length < 4)
      setSearchTerm('');
  };

  return (
    <div className="h-screen flex flex-col bg-slate-100 font-sans text-slate-800 overflow-hidden">
      {/* Header */}
      <header className="bg-white border-b px-6 py-3 flex items-center justify-between shadow-sm z-10">
        <div className="flex items-center gap-3">
          <div className="bg-blue-600 text-white p-2 rounded-xl shadow-sm">
            <Store className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900 tracking-tight leading-tight">
              Bản đồ Phân bổ Cửa hàng
            </h1>
            <p className="text-xs text-slate-500">Quản lý vị trí, doanh thu và xếp hạng merchant</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => fetchData(currentPage)}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Làm mới
          </button>
          <div className="text-sm bg-slate-50 border px-3 py-1.5 rounded-lg text-slate-600">
            Tổng cộng:{" "}
            <span className="font-bold text-blue-600">
              {paginationInfo.totalCount || shopsData.length}
            </span>{" "}
            cửa hàng
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex relative overflow-hidden">
        {/* Sidebar Bên Trái */}
        <div className="w-96 bg-slate-50 border-r flex flex-col z-10 shadow-lg">
          {/* Search & Filter */}
          <div className="p-3.5 bg-white border-b space-y-2.5">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Tìm Terminal ID(Tối thiểu 4 ký tự)"
                className="pl-9 pr-4 py-2 w-full border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none transition bg-white"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="flex items-center gap-1 overflow-x-auto pb-0.5 text-xs">
              <Filter className="h-3.5 w-3.5 text-slate-400 mr-1 flex-shrink-0" />
              {["ALL", "Micro", "Small", "Medium", "Large"].map((seg) => (
                <button
                  key={seg}
                  onClick={() => handleSelectedSegment(seg) }
                  className={`px-2.5 py-1 rounded-md font-medium whitespace-nowrap transition-colors ${
                    selectedSegment === seg
                      ? "bg-blue-600 text-white shadow-sm"
                      : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
                  }`}
                >
                  {seg === "ALL" ? "Tất cả" : seg}
                </button>
              ))}
            </div>
          </div>

          {/* Danh Sách Cửa Hàng */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {loading && (
              <div className="flex flex-col items-center justify-center py-12 text-slate-500 gap-2">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <span className="text-sm font-medium">Đang tải danh sách cửa hàng...</span>
              </div>
            )}

            {error && !loading && (
              <div className="p-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-xs flex items-start gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">{error}</p>
                  <button
                    onClick={() => fetchData(currentPage)}
                    className="mt-2 text-blue-600 underline font-semibold hover:text-blue-800"
                  >
                    Thử lại
                  </button>
                </div>
              </div>
            )}

            {!loading && !error && shopsData.length === 0 && (
              <div className="p-8 text-center text-slate-400 text-sm">
                Không tìm thấy cửa hàng phù hợp
              </div>
            )}

            {!loading &&
              !error &&
              shopsData.map((shop) => {
                const shopKey = shop.terminalID || shop.id;
                const isSelected =
                  selectedShop?.terminalID === shop.terminalID || selectedShop?.id === shop.id;

                return (
                  <div
                    key={shopKey}
                    onClick={() => setSelectedShop(shop)}
                    className={`bg-white rounded-xl border p-3.5 transition-all cursor-pointer shadow-sm hover:shadow-md ${
                      isSelected
                        ? "border-blue-500 ring-2 ring-blue-500/20 bg-blue-50/30"
                        : "border-slate-200 hover:border-blue-300"
                    }`}
                  >
                    <div className="flex justify-between items-center gap-2 mb-2">
                      {shop.terminalID ? (
                        <button
                          onClick={(e) => handleNavigateToRevenue(shop.terminalID, e)}
                          className="text-[11px] font-bold text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 bg-blue-50 px-2 py-0.5 rounded border border-blue-200/80"
                        >
                          {shop.terminalID}
                          <ExternalLink className="h-3 w-3" />
                        </button>
                      ) : (
                        <span className="text-[11px] text-slate-400 italic">
                          Chưa có Terminal ID
                        </span>
                      )}

                      <span
                        className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                          SEGMENT_COLORS[shop.segment] || SEGMENT_COLORS.Micro
                        }`}
                      >
                        {shop.segment}
                      </span>
                    </div>

                    <h3 className="font-bold text-slate-900 text-sm mb-1 line-clamp-1">
                      {shop.workspaceName}
                    </h3>

                    <p className="text-xs text-slate-500 mb-3 flex items-start gap-1 line-clamp-2 leading-relaxed">
                      <MapPin className="h-3.5 w-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
                      {shop.merchantAddress}
                    </p>

                    <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100 text-xs">
                      <div>
                        <span className="text-[10px] text-slate-400 block uppercase font-medium">
                          Doanh thu
                        </span>
                        <span className="font-bold text-emerald-600">
                          {formatCurrency(shop.totalRevenue)}
                        </span>
                      </div>

                      <div className="text-right">
                        <span className="text-[10px] text-slate-400 block uppercase font-medium">
                          Xếp hạng
                        </span>
                        <span className="font-semibold text-slate-700">#{shop.rank}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>

          {/* Phân trang Footer */}
          <div className="bg-white border-t px-3.5 py-2.5 flex items-center justify-between text-xs text-slate-600">
            <span className="font-medium text-slate-500">
              Trang <span className="text-slate-900 font-bold">{currentPage}</span> /{" "}
              {paginationInfo.totalPage || 1}
            </span>

            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1 || loading}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <ChevronsLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= (paginationInfo.totalPage || 1) || loading}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => handlePageChange(paginationInfo.totalPage)}
                disabled={currentPage >= (paginationInfo.totalPage || 1) || loading}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <ChevronsRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Bản Đồ Tương Tác */}
        <div className="flex-1 h-full w-full relative z-0">
          <MapContainer
            center={defaultCenter}
            zoom={12}
            scrollWheelZoom={true}
            className="h-full w-full"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {/* Controller di chuyển & tự động mở Popup */}
            <MapController selectedShop={selectedShop} markerRefs={markerRefs} />

            {!loading && (
              <MarkerClusterGroup
                chunkedLoading
                iconCreateFunction={createCustomClusterIcon}
                maxClusterRadius={60}
              >
                {shopsData.map((shop) => {
                  const shopKey = shop.terminalID || shop.id;

                  return (
                    <Marker
                      key={shopKey}
                      position={[shop.lat, shop.lng]}
                      ref={(ref) => {
                        if (ref) markerRefs.current[shopKey] = ref;
                      }}
                      eventHandlers={{
                        click: () => setSelectedShop(shop),
                      }}
                    >
                      {/* 🎨 DESIGN POPUP TRÂN BẢN ĐỒ CHUẨN XUẤT SẮC THEO ẢNH */}
                      <Popup className="custom-shop-popup">
                        <div className="p-2 min-w-[280px] max-w-[320px] font-sans">
                          {/* Header Popup: Badge & Rank */}
                          <div className="flex justify-between items-center mb-2">
                            <span
                              className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-md border ${
                                SEGMENT_COLORS[shop.segment] || SEGMENT_COLORS.Micro
                              }`}
                            >
                              {shop.segment}
                            </span>
                            <span className="text-[11px] font-bold text-amber-600 flex items-center gap-1">
                              <Award className="h-3.5 w-3.5 text-amber-500" /> #{shop.rank}
                            </span>
                          </div>

                          {/* Tên Cửa Hàng */}
                          <h4 className="font-extrabold text-slate-900 text-base mb-1.5 leading-snug">
                            {shop.workspaceName}
                          </h4>

                          {/* Địa chỉ */}
                          <p className="text-xs text-slate-500 mb-3 flex items-start gap-1.5 leading-relaxed">
                            <MapPin className="h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5" />
                            <span>{shop.merchantAddress}</span>
                          </p>

                          {/* Doanh thu */}
                          <div className="bg-slate-50 border border-slate-100 rounded-xl p-2.5 mb-3 flex justify-between items-center">
                            <span className="text-xs font-medium text-slate-500 flex items-center gap-1">
                              <DollarSign className="h-3.5 w-3.5 text-emerald-500" /> Doanh thu:
                            </span>
                            <span className="text-sm font-bold text-emerald-600">
                              {formatCurrency(shop.totalRevenue)}
                            </span>
                          </div>

                          {/* Terminal ID & Link chuyển sang Sổ Doanh Thu */}
                          <div className="pt-2 border-t border-slate-100 flex justify-between items-center text-xs">
                            <span className="text-slate-400 font-medium">Terminal ID:</span>
                            {shop.terminalID ? (
                              <button
                                onClick={(e) => handleNavigateToRevenue(shop.terminalID, e)}
                                className="font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1 bg-blue-50 hover:bg-blue-100 px-2.5 py-1 rounded-lg border border-blue-200 transition"
                              >
                                {shop.terminalID}
                                <ExternalLink className="h-3 w-3" />
                              </button>
                            ) : (
                              <span className="text-slate-400 italic">Chưa có ID</span>
                            )}
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MarkerClusterGroup>
            )}
          </MapContainer>
        </div>
      </div>
    </div>
  );
};

export default WorkspacesManagerNew;